import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useEffect, useRef } from 'react';
import type { PatientNote } from '../../../services/patientNotes';
import { useEscapeKey } from '../../../hooks/useEscapeKey';

interface DeleteNoteConfirmModalProps {
    note: PatientNote;
    isDeleting: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}

function DeleteNoteConfirmModal({
    note,
    isDeleting,
    onConfirm,
    onCancel
}: DeleteNoteConfirmModalProps) {
    // Autofocus the safe action (Cancel) — destructive defaults shouldn't be auto-focused.
    const cancelRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        cancelRef.current?.focus();
    }, []);

    useEscapeKey(onCancel, !isDeleting);

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-note-modal-title"
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm"
        >
            <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-6 relative">
                <button
                    onClick={onCancel}
                    disabled={isDeleting}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
                    aria-label="Close"
                >
                    <XMarkIcon className="w-5 h-5" />
                </button>

                <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                        <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3
                            id="delete-note-modal-title"
                            className="text-lg font-semibold text-gray-900"
                        >
                            Delete note?
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                            This action cannot be undone. The note{' '}
                            <span className="font-medium text-gray-700">"{note.title}"</span> will
                            be permanently removed.
                        </p>
                    </div>
                </div>

                <div className="flex justify-end items-center gap-3 mt-6">
                    <button
                        ref={cancelRef}
                        onClick={onCancel}
                        disabled={isDeleting}
                        className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={isDeleting}
                        className="px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                    >
                        {isDeleting ? (
                            <>
                                <div className="w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                                Deleting…
                            </>
                        ) : (
                            'Delete note'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}

export { DeleteNoteConfirmModal };
